import { useEffect, useState } from 'react';
import Modal from './Modal.tsx';
import { api, errorMessage } from '../lib/api.ts';
import { formatBuiltBy } from '../lib/format.ts';
import type { DeckStudioMeta, DeckSummary } from '../../shared/deck.ts';

/** `GET /api/decks/:slug/build-info` — the provenance the runners stamped. */
type BuildInfoPayload = {
  builtBy?: DeckStudioMeta['builtBy'];
  builtWith?: DeckStudioMeta['builtWith'];
};

/**
 * How a deck came to exist: which engine and model built it, and what the
 * author asked for.
 *
 * The second half is the point. Until 2026-08-06 the wizard answers lived only
 * in the intake bundle, which every engine deletes the moment a build
 * SUCCEEDS — so the settings survived for builds that failed and were destroyed
 * for the ones that worked. "Why is this deck all one colour", "did we tell it
 * to keep the client's words", "was that the 40-slide setting" had no answer.
 *
 * Fetched on open rather than ridden along on the deck list, which every
 * dashboard render pays for: this is a whole answer set, wanted one deck at a
 * time and rarely.
 *
 * A deck built before the capture existed says so. Never infer a default from
 * absence — "not recorded" and "they chose the default" look identical here and
 * are not the same thing.
 */

/** Wizard answer → what the author actually saw in the modal. Anything not
 *  listed is shown by its raw key, so a new wizard option appears here the day
 *  it ships rather than being silently dropped. */
const LABELS: Record<string, string> = {
  audience: 'Audience',
  format: 'Format',
  slideBudget: 'Length',
  slideCount: 'Target slides',
  sectionCount: 'Sections',
  sectionIntros: 'Section intros',
  colourTreatment: 'Colour',
  baseColour: 'Accent',
  colourMatchSource: 'Match my deck’s colours',
  verbatim: 'The author’s words',
  overflow: 'When content overruns',
  imagery: 'Imagery',
  video: 'Video',
};

/** The values that read as jargon on their own. */
const VALUES: Record<string, Record<string, string>> = {
  audience: { conference: 'Conference', pitch: 'Pitch', team: 'Team', workshop: 'Workshop' },
  format: { live: 'Presented live', readout: 'Read on their own' },
  slideBudget: { short: 'Short (10–15)', standard: 'Standard (20–35)', deep: 'Deep (40–60)' },
  colourTreatment: { single: 'One accent throughout', sections: 'A colour per section' },
  verbatim: {
    strict: 'Kept exactly',
    light: 'Kept, may be reformatted',
    source: 'Used as source material',
  },
  overflow: { split: 'Spread over more slides', condense: 'Tightened to fit' },
  imagery: { uploaded: 'The author’s uploads', stock: 'Stock photography', none: 'No photography' },
};

/** Keys that describe the REQUEST rather than an answer — shown elsewhere in
 *  this panel or not worth a row. */
// `referenceImages` joins them: a file list reads as noise here, and the
// archive already drops `docs`/`images` for the same reason.
const SKIP = new Set(['slug', 'title', 'createdAt', 'referenceImages']);

function renderValue(key: string, value: unknown): string {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  const mapped = VALUES[key]?.[String(value)];
  if (mapped) return mapped;
  // Accents are stored as brand TOKEN names (CYAN, ORANGE) because that is what
  // the deck's code imports. Shouted at the reader they look like an error, and
  // the wizard showed a swatch, not the token.
  if (key === 'baseColour') {
    const t = String(value);
    return t.charAt(0) + t.slice(1).toLowerCase();
  }
  return String(value);
}

export default function DeckInfoModal({ deck, onClose }: { deck: DeckSummary; onClose: () => void }) {
  const [info, setInfo] = useState<BuildInfoPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .get<BuildInfoPayload>(`/api/decks/${deck.slug}/build-info`)
      .then((d) => live && setInfo(d))
      .catch((err) => live && setError(errorMessage(err) || 'Could not read this deck’s build info'));
    return () => {
      live = false;
    };
  }, [deck.slug]);

  const builtBy = info?.builtBy;
  const builtWith = info?.builtWith;
  const answers = builtWith?.answers || {};
  const rows = Object.entries(answers).filter(([k]) => !SKIP.has(k));

  return (
    <Modal
      title="Deck info"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      <p className="muted small" style={{ marginBottom: '0.8rem' }}>
        {deck.title}
      </p>

      {error && <p className="error small">{error}</p>}
      {!info && !error && <p className="muted small">Reading…</p>}

      {info && (
        <>
          <h4 className="deck-info__heading">Built by</h4>
          {builtBy ? (
            <dl className="deck-info__list">
              <div className="deck-info__row">
                <dt>Engine</dt>
                <dd>{formatBuiltBy(builtBy) || `${builtBy.engine} · ${builtBy.model}`}</dd>
              </div>
              {builtBy.provider && (
                <div className="deck-info__row">
                  <dt>Provider</dt>
                  {/* The FULL stamped value, not the badge's shortened
                          host: this row is where someone comes to find out
                          exactly which endpoint served a deck. */}
                  <dd>{builtBy.provider}</dd>
                </div>
              )}
              {builtBy.at && (
                <div className="deck-info__row">
                  <dt>Built</dt>
                  <dd>{new Date(builtBy.at).toLocaleString('en-GB')}</dd>
                </div>
              )}
              {builtBy.auth === 'api' && (
                <div className="deck-info__row">
                  <dt>Billing</dt>
                  <dd>Metered API key</dd>
                </div>
              )}
            </dl>
          ) : (
            <p className="muted small">
              Not recorded. This deck was made by hand, imported, or built before the studio started keeping provenance.
            </p>
          )}

          <h4 className="deck-info__heading">Asked for</h4>
          {builtWith ? (
            <>
              <dl className="deck-info__list">
                {rows.map(([key, value]) => (
                  <div className="deck-info__row" key={key}>
                    <dt>{LABELS[key] || key}</dt>
                    <dd>{renderValue(key, value)}</dd>
                  </div>
                ))}
              </dl>
              {((builtWith.docs || []).length > 0 || (builtWith.images || []).length > 0) && (
                <dl className="deck-info__list">
                  {(builtWith.docs || []).length > 0 && (
                    <div className="deck-info__row">
                      <dt>Brief</dt>
                      <dd>{(builtWith.docs || []).map((d) => d.split('/').pop()).join(', ')}</dd>
                    </div>
                  )}
                  {(builtWith.images || []).length > 0 && (
                    <div className="deck-info__row">
                      <dt>Uploads</dt>
                      <dd>
                        {(builtWith.images || []).length} image{(builtWith.images || []).length === 1 ? '' : 's'}
                      </dd>
                    </div>
                  )}
                </dl>
              )}
            </>
          ) : (
            <p className="muted small">
              Not recorded. The studio only started keeping the wizard answers on 6 August 2026 — before that they were
              deleted along with the brief when a build succeeded. Decks built from now on will show them here.
            </p>
          )}
        </>
      )}
    </Modal>
  );
}
